<script lang="ts">
	import { Image } from "@unpic/svelte";
	import { Indicator, Spinner } from "flowbite-svelte";
	import { ImagePlaceholder } from "flowbite-svelte-v1";
	import { createEventDispatcher, getContext } from "svelte";
	import { twJoin, twMerge } from "tailwind-merge";
	import type { Suite } from "#gen/bundles/v1/bundle_pb.js";
	import type { Point } from "#gen/bundles/v1/common_pb.js";
	import type { EntryAsset } from "#lib/assetManager.js";
	import Icon from "#lib/components/Icon.svelte";
	import AddImage from "#lib/components/images/AddImage.svelte";
	import { LANDSCAPE_IMAGE_ASPECT_RATIO, UPLOADABLE_IMAGE_FILE_TYPES } from "#lib/components/images/constants.js";
	import FileManagerDialog from "#lib/components/images/file-management/FileManagerDialog.svelte";
	import type { ImagePickerChangeEvent } from "#lib/components/images/types.js";
	import { convertPDFOrEPSImage, requireImageConversion, transformURL } from "#lib/image.js";
	import { interpolate } from "#lib/stores/asset.js";
	import Button from "#lib/ui/Button/index.js";
	import type { Writable } from "svelte/store";

	interface $$Events {
		change: CustomEvent<ImagePickerChangeEvent>;
	}

	const dispatch = createEventDispatcher();
	const suite: Writable<Suite> = getContext("suite");
	const entryAsset: Writable<EntryAsset | undefined> = getContext("entryAsset");

	export let value: string | undefined = undefined;
	export let imgClass = "";
	export let dropzoneClass = "h-full";
	export let aspectRatio: number | undefined = undefined;
	export let initialAspectRatio: number | undefined = undefined;
	export let cropperCaption: string | undefined = undefined;
	export let shape: "circle" | "rectangle" | undefined = undefined;
	export let finalWidth: number | undefined = undefined;
	export let finalHeight: number | undefined = undefined;
	export let imageTypeCaption = "image";
	export let fileTypes: string[] = UPLOADABLE_IMAGE_FILE_TYPES;
	export let closeButton = true;
	export let disabled = false;
	export let allowFocalPointSelection = false;
	export let fromOrgAdmin = false;
	export let showAspectRatioPicker = false;
	export let preferFreeformAspectRatio = false;

	let preview: string | null | undefined = value;
	let editingProperties = false;
	let imageInvalid = false;
	let renderedValue: string | undefined;
	let focalPoint: Point | undefined = undefined;

	$: {
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		value;
		imageInvalid = false;
	}

	$: if (value !== renderedValue) {
		if (value) {
			renderPreview(value);
		} else {
			preview = undefined;
			renderedValue = undefined;
		}
	}

	function valueChanged(e: CustomEvent<ImagePickerChangeEvent>) {
		value = e.detail.value;
		focalPoint = e.detail.focalPoint;
		if (value) {
			renderPreview(value);
		} else {
			preview = undefined;
			renderedValue = undefined;
		}
		dispatch("change", e.detail);
		editingProperties = false;
	}

	async function renderPreview(url: string) {
		if ($suite && url.includes("{{")) {
			try {
				preview = interpolate($suite, entryAsset, url);
			} catch {
				preview = url;
			}
		} else {
			preview = url;
		}
		renderedValue = url;
	}

	function reset() {
		value = "";
		focalPoint = undefined;
		preview = "";
		dispatch("change", { value: "", contentType: null });
	}
</script>

{#if editingProperties && value !== undefined}
	<FileManagerDialog bind:open={editingProperties} on:change={valueChanged} {focalPoint} {imageTypeCaption} {value} {aspectRatio} {initialAspectRatio} {shape} caption={cropperCaption} {finalWidth} {finalHeight} {fileTypes} {allowFocalPointSelection} {fromOrgAdmin} {showAspectRatioPicker} {preferFreeformAspectRatio} />
{/if}

<div class={twMerge(`group/edit-image relative w-full border bg-gray-50`, $$props.class)} style="aspect-ratio: {aspectRatio ?? LANDSCAPE_IMAGE_ASPECT_RATIO};">
	{#if value}
		<div class={twJoin("flex rounded-lg", shape !== "circle" && "h-full overflow-clip")}>
			<Button {disabled} color="none" class={twJoin("w-full cursor-pointer overflow-clip border  p-0", shape === "circle" ? "rounded-full" : "h-full", disabled ? "cursor-default opacity-100" : "")} on:click={() => (editingProperties = true)} data-action-name="edit-image">
				{#if !preview || imageInvalid}
					<ImagePlaceholder size="sm" imgOnly={true} class="h-full w-full animate-none" classes={{ image: "h-full sm:w-full w-full bg-gray-50 m-0", svg: "text-gray-400 h-4 w-4" }} />
				{:else if requireImageConversion(preview ?? value, "")}
					{#await convertPDFOrEPSImage(preview ?? value, "")}
						<div class="flex h-full w-full items-center justify-center">
							<Spinner size={8} aria-hidden={true} />
						</div>
					{:then svgUrl}
						{#if svgUrl}
							<Image layout="fullWidth" {aspectRatio} alt="new" src={svgUrl} class={twMerge("text-transparent", imgClass)} onerror={() => (imageInvalid = true)} style="object-fit: contain;" />
						{/if}
					{:catch}
						<div class="flex h-full w-full items-center justify-center text-sm text-gray-500">Error loading EPS preview</div>
					{/await}
				{:else}
					<Image layout="fullWidth" {aspectRatio} alt="new" src={transformURL(preview ?? value)} class={twMerge("text-transparent", imgClass)} onerror={() => (imageInvalid = true)} style="object-fit: contain;" />
				{/if}
			</Button>
			{#if closeButton && !disabled}
				<Indicator size="xl" placement="top-right" class="z-10 hidden group-hover/edit-image:inline">
					<Button color="none" on:click={reset} data-action-name="remove-image" class="m-auto cursor-pointer p-1">
						<Icon iconSet="heroicons" name="x-mark" class="h-4 w-4" />
					</Button>
				</Indicator>
			{/if}
		</div>
	{:else}
		<AddImage {dropzoneClass} on:change={valueChanged} {initialAspectRatio} {aspectRatio} caption={cropperCaption} {imageTypeCaption} {shape} {finalWidth} {finalHeight} {fileTypes} {disabled} {fromOrgAdmin} {showAspectRatioPicker} {preferFreeformAspectRatio}>
			<slot />
		</AddImage>
	{/if}
</div>
